import { useState, useRef, useEffect } from 'react';
import { Settings } from 'lucide-react';
import type { EntityColumnConfig } from '@/config/entityColumns';

interface ColumnSelectorProps {
  allColumns: EntityColumnConfig[];
  selectedKeys: string[];
  onToggle: (key: string) => void;
  onReset: () => void;
  isAtMax: boolean;
  maxColumns: number;
}

export function ColumnSelector({ allColumns, selectedKeys, onToggle, onReset, isAtMax, maxColumns }: ColumnSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setIsOpen(!isOpen)}
        className="btn btn-outline p-2 min-h-0" title="Configure columns">
        <Settings className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-2">
          <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">
            Columns ({selectedKeys.length}/{maxColumns})
          </div>
          <div className="max-h-64 overflow-y-auto">
            {allColumns.map((col) => {
              const isSelected = selectedKeys.includes(col.key);
              const isDisabled = !isSelected && isAtMax;
              return (
                <label key={col.key}
                  className={`flex items-center px-3 py-1.5 hover:bg-gray-50 cursor-pointer ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input type="checkbox" checked={isSelected} disabled={isDisabled}
                    onChange={() => onToggle(col.key)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">{col.header}</span>
                </label>
              );
            })}
          </div>
          <div className="border-t border-gray-200 px-3 py-2">
            <button type="button" onClick={onReset}
              className="text-xs text-primary-600 hover:text-primary-700">
              Reset to Default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
